import type { BimWorkOrderPriceData } from 'gql/graphql';
import { ContractStatusEnum, ProjectStatusEnum, WorkOrderStatusEnum } from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';
import { Button, ContextMenu, IconKebab } from 'uikit';

import * as R from 'ramda';
import type React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { BimWorkOrderPriceMtrDistEdit } from '../../BimWorkOrderPriceMtrDistEdit/BimWorkOrderPriceMtrDistEdit';
import { BimWorkOrderRowDetailsType } from '../../types';

interface IBimWorkOrderPriceTableContextMenu {
    data: BimWorkOrderPriceData;
    makeRefreshTable: () => void;
    openDetails: (historyType?: BimWorkOrderRowDetailsType) => void;
    openActs?: () => void;
    workOrderStatus?: WorkOrderStatusEnum;
    contractStatus?: ContractStatusEnum;
    orderRules?: IUseAccess['orderBim'];
    statusProject?: ProjectStatusEnum;
    hasInProgress?: boolean;
}

export const BimWorkOrderPriceTableContextMenu: React.FC<IBimWorkOrderPriceTableContextMenu> = (props) => {
    const {
        data,
        makeRefreshTable,
        openDetails,
        statusProject,
        openActs,
        workOrderStatus,
        contractStatus,
        orderRules,
        hasInProgress,
    } = props;
    const [isContextOpen, setIsContextOpen] = useState<boolean>(false);
    const buttonRef = useRef(null);

    const { versionId } = useParams();
    const isVersionPage = !!versionId;

    const [bimWorkOrderPriceMtrDistEditOpen, setBimWorkOrderPriceMtrDistEditOpen] = useState(false);

    const isContractStatusDelayed = contractStatus === ContractStatusEnum.Delayed;
    const isContractStatusCompleted = contractStatus === ContractStatusEnum.Completed;
    const isWorkOrderStatusOnFormation = workOrderStatus === WorkOrderStatusEnum.OnFormation;
    const isProjectStatusDelayed = statusProject === ProjectStatusEnum.Delayed;

    const onContextMenuButtonClick = useCallback((evt) => {
        evt.stopPropagation();
        setIsContextOpen(R.not);
    }, []);

    const onBimWorkOrderPriceMtrDistEditClose = useCallback(
        (isSuccess: boolean) => {
            setBimWorkOrderPriceMtrDistEditOpen(false);

            if (isSuccess) {
                makeRefreshTable();
            }
        },
        [makeRefreshTable],
    );

    const isDisabledRedeployMTR =
        !isWorkOrderStatusOnFormation ||
        isContractStatusDelayed ||
        isContractStatusCompleted ||
        !orderRules?.canEdit ||
        isProjectStatusDelayed;

    const isAnalyticsAvailable = useMemo(
        () =>
            data.hasRevisions &&
            workOrderStatus &&
            ((hasInProgress && workOrderStatus === WorkOrderStatusEnum.Delayed) ||
                [WorkOrderStatusEnum.InProgress, WorkOrderStatusEnum.Completed].includes(workOrderStatus)),
        [data.hasRevisions, hasInProgress, workOrderStatus],
    );

    const kebabMenuItems = useMemo(
        () => [
            ...[
                {
                    label: 'Перераспределить МТР',
                    action: () => setBimWorkOrderPriceMtrDistEditOpen(true),
                    disabled: isDisabledRedeployMTR,
                },
            ],
            ...(data.hasActKC2
                ? [
                      {
                          label: 'Просмотреть Акты КС-2',
                          action: openActs,
                      },
                  ]
                : []),
            ...(data.hasRevisions
                ? [
                      {
                          label: 'Просмотреть Ревизии сметы',
                          action: () => openDetails(BimWorkOrderRowDetailsType.Revision),
                      },
                  ]
                : []),
            ...(data.hasUpdates
                ? [
                      {
                          label: 'Просмотреть обновления по смете',
                          action: () => openDetails(BimWorkOrderRowDetailsType.Update),
                      },
                  ]
                : []),
            ...(isAnalyticsAvailable
                ? [
                      {
                          label: 'Просмотреть аналитику изменения сметы',
                          action: () => openDetails(BimWorkOrderRowDetailsType.Analytics),
                      },
                  ]
                : []),
        ],
        [
            data.hasActKC2,
            data.hasRevisions,
            data.hasUpdates,
            isAnalyticsAvailable,
            isDisabledRedeployMTR,
            openActs,
            openDetails,
        ],
    );

    return (
        <>
            <Button
                iconLeft={IconKebab}
                onlyIcon
                view="clear"
                size="xs"
                ref={buttonRef}
                onClick={onContextMenuButtonClick}
                disabled={isVersionPage}
            />
            <ContextMenu
                isOpen={isContextOpen}
                items={kebabMenuItems}
                anchorRef={buttonRef}
                direction="downStartLeft"
                onClickOutside={onContextMenuButtonClick}
                getItemOnClick={({ action }) => action}
            />
            {bimWorkOrderPriceMtrDistEditOpen && (
                <BimWorkOrderPriceMtrDistEdit
                    workOrderPriceId={data.id}
                    onClose={onBimWorkOrderPriceMtrDistEditClose}
                />
            )}
        </>
    );
};

BimWorkOrderPriceTableContextMenu.displayName = 'BimWorkOrderPriceTableContextMenu';
